import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components'
import { PlatformIcon, platformLabel } from 'components/PlatformIcon'
import {
  CalendarOff,
  Check,
  Ellipsis,
  ExternalLink,
  Image as ImageIcon,
  Layers,
  Loader2,
  Pencil,
  Sparkles,
  Trash2,
  Upload,
  Video,
} from 'lucide-react'
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from 'lib/utils'
import type { ContentShape } from '../store/idea-store'
import type { PublishFailureCopy } from '../utils/publish-failure-copy'

/**
 * Corner chips shared by every post-card surface (Home/Posts `IdeaCard`, the
 * Weekly Cockpit `QueuedPostCard`). Keeping them here is the single source of
 * truth for the shape-badge and live-indicator language so the tiles read
 * identically across surfaces.
 */

export const SHAPE_ICON: Record<ContentShape, typeof Sparkles> = {
  static_image: ImageIcon,
  short_vertical_video: Video,
  story: Sparkles,
  carousel: Layers,
  long_form_video: Video,
}

export const SHAPE_LABEL: Record<ContentShape, string> = {
  static_image: 'Image',
  short_vertical_video: 'Reel',
  story: 'Story',
  carousel: 'Carousel',
  long_form_video: 'Long video',
}

/**
 * Coerce a raw DTO `shape` string (cockpit queue item / calendar entry) to a
 * known `ContentShape`, falling back to `static_image` for anything unexpected.
 */
export function normalizeShape(shape: string | undefined): ContentShape {
  return shape && shape in SHAPE_LABEL ? (shape as ContentShape) : 'static_image'
}

/**
 * Top-left shape badge — a mono capsule notched into the card's corner naming
 * the post's media shape (Image / Reel / Carousel…). Overlaid on the asset.
 *
 * `iconOnly` drops the text label and renders a compact square chip — used on
 * the Weekly Cockpit `QueuedPostCard`, whose tiles are too small to carry both
 * the icon and the label (IdeaCard, with more room, keeps the full label).
 */
export function PostCardShapeBadge({
  shape,
  iconOnly = false,
}: {
  shape: ContentShape
  iconOnly?: boolean
}) {
  const ShapeIcon = SHAPE_ICON[shape]
  return (
    <div
      className={cn(
        'absolute top-0 left-0 z-20 inline-flex items-center rounded-br-[8px] border-r border-b border-line bg-bg-2 text-paper',
        iconOnly ? 'justify-center p-1.5' : 'mono gap-1 px-2 py-1 text-[11px] font-medium'
      )}
      aria-label={iconOnly ? SHAPE_LABEL[shape] : undefined}
      title={iconOnly ? SHAPE_LABEL[shape] : undefined}
    >
      <ShapeIcon size={iconOnly ? 14 : 12} />
      {!iconOnly && SHAPE_LABEL[shape]}
    </div>
  )
}

/**
 * Top-right "Yours" chip — marks a bring-your-own-content post (user-uploaded
 * media that Taka publishes untouched) so the calendar reads generated vs.
 * brought honestly. Mirrors `PostCardShapeBadge`'s notched capsule, anchored
 * to the opposite corner. Once the post is live the Live chip owns the
 * top-right corner instead — callers render Yours only while not published.
 */
export function PostCardYoursChip() {
  return (
    <div
      className="mono absolute top-0 right-0 z-20 inline-flex items-center gap-1 rounded-bl-[8px] border-b border-l border-line bg-bg-2 px-2 py-1 text-[11px] font-medium text-paper"
      title="You uploaded this post. Taka publishes it exactly as is."
    >
      <Upload size={10} />
      Yours
    </div>
  )
}

/** Footprint of one 16px platform icon plus its `gap-1` (4px) spacing. */
const PLATFORM_ICON_SIZE = 16
const PLATFORM_ICON_STEP = PLATFORM_ICON_SIZE + 4

/**
 * The row that sits directly below a small post-card's asset (Weekly Cockpit
 * `QueuedPostCard`, Calendar `CalendarPopoverCard`): left-aligned platform brand
 * icons and a right-aligned `action` slot (the ••• options menu) on one line.
 *
 * The `action` is the highest-priority element — always rendered. The icons are
 * lower priority: a `ResizeObserver` measures the space left of the action and
 * shows as many 16px icons as fit, collapsing any remainder into a trailing
 * `+N` chip. `visibleCount` starts at "all" so the common (everything fits)
 * case never corrects/flashes; `overflow-hidden` clips the brief pre-measure
 * frame in the rare case it doesn't.
 */
export function PostCardPlatformRow({
  platforms,
  action,
  failedPlatforms,
  publishedPlatforms,
}: {
  platforms: string[]
  action?: ReactNode
  /**
   * Platforms whose sibling permanently failed to publish. Renders a small
   * destructive dot on the matching platform icon so the row hints at where
   * the failure lives even before the failure modal opens. Mirrors the
   * not-connected dot pattern from PostModalPlatformRow but destructive-colored.
   */
  failedPlatforms?: string[]
  /**
   * Platforms whose sibling already published — small acid (success) dot,
   * so a partially-published post reads green-vs-red at a glance.
   */
  publishedPlatforms?: string[]
}) {
  const failedSet = useMemo(() => new Set(failedPlatforms ?? []), [failedPlatforms])
  const publishedSet = useMemo(() => new Set(publishedPlatforms ?? []), [publishedPlatforms])
  // Failed platforms must NEVER hide inside the `+N` overflow — they carry the
  // signal the user has to act on. Order: failed, then published, then rest
  // (stable within each group).
  const unique = useMemo(() => {
    const all = [...new Set(platforms)]
    const rank = (p: string) => (failedSet.has(p) ? 0 : publishedSet.has(p) ? 1 : 2)
    return all.sort((a, b) => rank(a) - rank(b))
  }, [platforms, failedSet, publishedSet])
  const iconsRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(unique.length)

  useLayoutEffect(() => {
    const el = iconsRef.current
    if (!el) return
    const compute = () => {
      const width = el.clientWidth
      // How many icons fit if no overflow chip is needed.
      const fitAll = Math.floor((width + 4) / PLATFORM_ICON_STEP)
      if (fitAll >= unique.length) {
        setVisibleCount(unique.length)
        return
      }
      // Overflow: reserve one icon-step for the `+N` chip, then refit.
      const fit = Math.max(0, Math.floor((width + 4 - PLATFORM_ICON_STEP) / PLATFORM_ICON_STEP))
      setVisibleCount(fit)
    }
    compute()
    const ro = new ResizeObserver(compute)
    ro.observe(el)
    return () => ro.disconnect()
  }, [unique.length])

  if (unique.length === 0 && !action) return null

  // Failed platforms are ALWAYS shown — the `+N` overflow only ever collapses
  // NON-failed icons. Failed fill the front of the budget; any remaining slots
  // go to non-failed, and `+N` counts only the non-failed that didn't fit. (A
  // plain `slice(0, visibleCount)` could still bury a failure in `+N` when there
  // are more failed platforms than fit alongside the published/rest ones.)
  const failedVisible = unique.filter(p => failedSet.has(p))
  const nonFailed = unique.filter(p => !failedSet.has(p))
  const restSlots = Math.max(0, visibleCount - failedVisible.length)
  const shownRest = nonFailed.slice(0, restSlots)
  const visible = [...failedVisible, ...shownRest]
  const overflow = nonFailed.length - shownRest.length

  return (
    <div className="flex items-center justify-between gap-2">
      <div ref={iconsRef} className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
        {visible.map(p => (
          <span key={p} className="relative inline-flex">
            <PlatformIcon channel={p} size={PLATFORM_ICON_SIZE} />
            {failedSet.has(p) ? (
              <span
                aria-label={`${platformLabel(p)} failed to publish`}
                title={`${platformLabel(p)} failed to publish`}
                // Sits INSIDE the icon bounds: the icons row is overflow-hidden
                // (required by the +N collapse measurement), so anything poking
                // past the icon's top/right edge gets clipped to a half-moon.
                className="absolute top-0 right-0 h-1.5 w-1.5 rounded-full bg-destructive ring-[1.5px] ring-[color:var(--bg-2)]"
              />
            ) : publishedSet.has(p) ? (
              <span
                aria-label={`${platformLabel(p)} published`}
                title={`${platformLabel(p)} published`}
                className="absolute top-0 right-0 h-1.5 w-1.5 rounded-full bg-acid ring-[1.5px] ring-[color:var(--bg-2)]"
              />
            ) : null}
          </span>
        ))}
        {overflow > 0 && (
          <span className="inline-flex h-4 shrink-0 items-center justify-center rounded-[4px] border border-line bg-bg-3 px-1 text-[10px] leading-none font-medium text-paper-dim">
            +{overflow}
          </span>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}

/**
 * Top-right live indicator. Compact at rest (a pulsing dot in a solid dark
 * capsule that reads on any image); expands to the "Live" label on card hover.
 * The width animation is driven by the parent card's `group` hover. Used only
 * for posts that are already published / mid-publish.
 */
export function PostCardLiveChip({ label = 'Live' }: { label?: string }) {
  return (
    <div className="mono inline-flex h-8 min-w-[32px] items-center justify-center rounded-full border border-line bg-bg-2 px-1.5 text-[11px] font-medium text-paper shadow-sm">
      <span className="liveDot shrink-0" aria-hidden />
      <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 group-hover:max-w-[160px] group-hover:pl-1.5 group-hover:opacity-100">
        {label}
      </span>
    </div>
  )
}

/**
 * One failed sibling that should be surfaced through the shared failure chip.
 * `creativeId` keys the pill so pusher failure events route to the right one;
 * `reason` is the machine token — NEVER render raw, always feed through
 * `getPublishFailureCopy`.
 */
export interface FailedPlatform {
  creativeId: string
  platform: string
  reason?: string
}

/**
 * Minimal `PlatformStatusDto` slice this module reads. Kept local so callers
 * pass whatever DTO shape they hold (cockpit queue item vs calendar entry).
 */
interface PlatformStatusLike {
  creativeId: string
  platform: string
  status: string
  failureReason?: string
}

/**
 * `platformStatuses` → `FailedPlatform[]` for the shared chip. One home for
 * the "filter status === failed, map to reason" derivation so QueuedPostCard
 * and CalendarPopoverCard can't drift on the shape. Absent/empty input yields
 * an empty array (drives the chip's own null render).
 */
export function toFailedPlatforms(
  platformStatuses?: PlatformStatusLike[] | null
): FailedPlatform[] {
  if (!platformStatuses || platformStatuses.length === 0) return []
  return platformStatuses
    .filter(p => p.status === 'failed')
    .map(p => ({ creativeId: p.creativeId, platform: p.platform, reason: p.failureReason }))
}

/** `platformStatuses` → platform names already published (success dots on the icon row). */
export function toPublishedPlatformNames(platformStatuses?: PlatformStatusLike[] | null): string[] {
  if (!platformStatuses || platformStatuses.length === 0) return []
  return platformStatuses.filter(p => p.status === 'published').map(p => p.platform)
}

/**
 * Renders one failure reason (title + body + optional Reconnect link) inside
 * the PostModal per-row alert popover. The link uses react-router `Link` — the
 * modal used to use `<a href>` which triggered a full reload on click.
 * NOTE: this is only for GENERIC failure reasons. The PostModal `account_
 * restricted` row keeps its bespoke external-unlock chrome (pixel-parity).
 */
export function PublishFailureCopyBlock({ copy }: { copy: PublishFailureCopy }) {
  return (
    <>
      <p className="text-[13px] font-medium text-[color:var(--alert-error-text)]">{copy.title}</p>
      <p className="text-[12px] leading-snug text-paper-dim">{copy.body}</p>
      {copy.ctaLabel && copy.href && (
        <Link
          to={copy.href}
          onClick={e => e.stopPropagation()}
          className="mt-0.5 inline-flex items-center gap-1 text-[12px] font-medium text-paper-dim underline underline-offset-2 hover:text-paper"
        >
          {copy.ctaLabel}
          <ExternalLink size={11} />
        </Link>
      )}
    </>
  )
}

/**
 * Top-right destructive failure chip — the visual peer of `PostCardLiveChip`
 * but for permanently failed publishes. Compact at rest (a red twin of the
 * Live dot in a solid dark capsule so it reads on any image); expands to a
 * short label on card hover. Clicking opens the post's own PostModal via
 * `onOpenPost` — the SAME dialog a card click opens — where the per-platform
 * failure rows carry the reason copy, Reconnect links, and Retry. No bespoke
 * failure dialog: PostModal is the one recovery hub.
 *
 * The chip stops click/pointer-down propagation so the open never doubles as
 * a card click (in ScheduledPostCard the chip sits OUTSIDE the clickable
 * article, so it must trigger the open itself anyway).
 */
export function PostCardFailedChip({
  failedPlatforms,
  onOpenPost,
}: {
  failedPlatforms: FailedPlatform[]
  /** Opens the post's PostModal. Omitted = the chip is a passive indicator. */
  onOpenPost?: () => void
}) {
  if (failedPlatforms.length === 0) return null

  return (
    <button
      type="button"
      data-testid="post-failed-chip"
      onClick={e => {
        e.stopPropagation()
        onOpenPost?.()
      }}
      onPointerDown={e => e.stopPropagation()}
      aria-label={
        failedPlatforms.length === 1
          ? `${platformLabel(failedPlatforms[0].platform)} publish failed`
          : `${failedPlatforms.length} platforms failed to publish`
      }
      className={cn(
        'mono inline-flex h-8 min-w-[32px] items-center justify-center rounded-full border border-line bg-bg-2 px-1.5 text-[11px] font-medium text-[color:var(--alert-error-text)] shadow-sm',
        onOpenPost && 'cursor-pointer'
      )}
    >
      {/* Red twin of the Live dot (not an alert glyph) — a failed post reads
          as "live but wrong" at a glance. */}
      <span className="failedDot shrink-0" aria-hidden />
      <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 group-hover:max-w-[160px] group-hover:pl-1.5 group-hover:opacity-100">
        Failed
      </span>
    </button>
  )
}

/**
 * Amber "held" chip — the honest state for a post whose slot passed without
 * publishing because a server-side gate held it (missed approval, lapsed
 * subscription, paused plan). Same silhouette as the failed chip so the corner
 * vocabulary stays consistent; expands to its label on hover. Clicking opens
 * PostModal (the recovery hub) when a handler is provided.
 */
export function PostCardHeldChip({
  label,
  title,
  onOpenPost,
}: {
  /** Short chip label, e.g. "Missed approval". */
  label: string
  /** Tooltip explaining the hold + the way out. */
  title?: string
  onOpenPost?: () => void
}) {
  return (
    <button
      type="button"
      data-testid="post-held-chip"
      title={title}
      onClick={e => {
        e.stopPropagation()
        onOpenPost?.()
      }}
      onPointerDown={e => e.stopPropagation()}
      aria-label={title ?? label}
      className={cn(
        'mono inline-flex h-8 min-w-[32px] items-center justify-center rounded-full border border-line bg-bg-2 px-1.5 text-[11px] font-medium text-amber-400 shadow-sm',
        onOpenPost && 'cursor-pointer'
      )}
    >
      <span className="h-2 w-2 shrink-0 rounded-full bg-amber-400" aria-hidden />
      <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 group-hover:max-w-[180px] group-hover:pl-1.5 group-hover:opacity-100">
        {label}
      </span>
    </button>
  )
}

/**
 * Hover scrim — a Pinterest-style blackish gradient veil between the media and
 * the overlaid controls so chips/buttons read on any image. Raw black (not a bg
 * token) on purpose: an overlay over user media must darken reliably regardless
 * of theme (Tailwind v4 + opacity on custom utilities emits no CSS). Driven by
 * the parent card's `group` hover. `edge` anchors it top (badges) or bottom (the
 * ••• menu / Visit); pass `className` to override the default `h-20` height.
 */
export function PostCardHoverScrim({
  edge = 'top',
  className,
}: {
  edge?: 'top' | 'bottom'
  className?: string
}) {
  return (
    <div
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-x-0 z-10 h-20 opacity-0 transition-opacity duration-200 group-hover:opacity-100',
        edge === 'top'
          ? 'top-0 bg-gradient-to-b from-black/80 via-black/25 to-transparent'
          : 'bottom-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent',
        className
      )}
    />
  )
}

/**
 * Shared dark dropdown-item recipe for post-card ••• menus. Overrides the base
 * item's legacy `focus:bg-accent` with T3 surfaces (twMerge only dedupes
 * matching variants, so the `focus:` overrides must be set explicitly).
 */
export const POST_CARD_MENU_ITEM_CLASS =
  'text-paper-dim focus:bg-[var(--bg-4)] focus:text-[var(--paper)] data-[highlighted]:bg-[var(--bg-4)] data-[highlighted]:text-[var(--paper)]'

/** Destructive variant of the dropdown-item recipe (the Delete action). */
const POST_CARD_MENU_DELETE_ITEM_CLASS =
  'text-[color:var(--alert-error-text)] focus:bg-[color:var(--alert-error-bg)] focus:text-[color:var(--alert-error-text)] data-[highlighted]:bg-[color:var(--alert-error-bg)] data-[highlighted]:text-[color:var(--alert-error-text)]'

/** Default ••• trigger styling for the in-row menu (cockpit / calendar). */
const MENU_TRIGGER_ROW_CLASS =
  'text-paper-dim hover:bg-bg-3 hover:text-paper data-[state=open]:bg-bg-3 data-[state=open]:text-paper flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors'

/**
 * The ••• options menu shared by every post-card surface — an Edit / Unschedule
 * / Delete dropdown that opens upward. Defaults to the in-row trigger styling
 * (cockpit/calendar); pass `triggerClassName` to override it entirely (IdeaCard's
 * in-media overlay button). `canEdit` / `canDelete` gate Edit/Delete
 * independently; the Unschedule item renders only when `onUnschedule` is passed
 * (containers omit it for non-scheduled posts). Unschedule is reversible (the
 * post becomes a draft), so it uses the neutral item recipe, not the destructive
 * one. The trigger stops `pointerdown` (unblocks Calendar's draggable parent; a
 * no-op elsewhere) and `click` (so opening the menu never opens the card).
 * Returns null when no action is available.
 */
export function PostCardMenu({
  onEdit,
  onUnschedule,
  onDelete,
  canEdit = true,
  canDelete = true,
  disabled,
  triggerClassName,
}: {
  onEdit?: () => void
  onUnschedule?: () => void
  onDelete?: () => void
  canEdit?: boolean
  canDelete?: boolean
  disabled?: boolean
  triggerClassName?: string
}) {
  if (!(canEdit && onEdit) && !onUnschedule && !(canDelete && onDelete)) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label="Post options"
          onPointerDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
          className={triggerClassName ?? MENU_TRIGGER_ROW_CLASS}
        >
          <Ellipsis size={16} />
        </button>
      </DropdownMenuTrigger>
      {/* Radix portals the menu, but React synthetic events still bubble the
          React tree — stop here so item clicks don't also open the card. */}
      <DropdownMenuContent
        variant="dark"
        align="end"
        side="top"
        className="w-40"
        onClick={e => e.stopPropagation()}
      >
        {canEdit && onEdit && (
          <DropdownMenuItem onSelect={onEdit} className={POST_CARD_MENU_ITEM_CLASS}>
            <Pencil size={14} className="mr-2" />
            Edit
          </DropdownMenuItem>
        )}
        {onUnschedule && (
          <DropdownMenuItem onSelect={onUnschedule} className={POST_CARD_MENU_ITEM_CLASS}>
            <CalendarOff size={14} className="mr-2" />
            Unschedule
          </DropdownMenuItem>
        )}
        {canDelete && onDelete && (
          <DropdownMenuItem onSelect={onDelete} className={POST_CARD_MENU_DELETE_ITEM_CLASS}>
            <Trash2 size={14} className="mr-2" />
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * Acid Approve CTA for review-mode post cards (Weekly Cockpit, Calendar). Owns
 * its own busy state with a mounted guard, and stops `pointerdown` so a
 * draggable parent (Calendar) doesn't swallow the click. `disabled` lets the
 * parent also block it while an unrelated action is pending (the cockpit's
 * per-item pending-action state).
 */
export function PostCardApproveButton({
  onApprove,
  disabled,
}: {
  onApprove: () => Promise<void> | void
  disabled?: boolean
}) {
  const [approving, setApproving] = useState(false)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const handleClick = async () => {
    if (approving) return
    setApproving(true)
    try {
      await onApprove()
    } finally {
      if (mountedRef.current) setApproving(false)
    }
  }

  return (
    <Button
      variant="t3-primary"
      size="sm"
      onPointerDown={e => e.stopPropagation()}
      onClick={e => {
        e.stopPropagation()
        void handleClick()
      }}
      disabled={disabled || approving}
      className="mt-auto !h-7 w-full !rounded-[6px] px-2 text-[11px]"
      title="Approve: publishes at the scheduled time"
      aria-label="Approve post"
      data-testid="approve-post"
    >
      {approving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
      Approve
    </Button>
  )
}
